// Keep in step with apps/canvas-builder/src/helperFunctions/restoreAuthReturnPath.js and apps/analytics-reports/src/helper/restoreAuthReturnPath.js.

const AUTH_HASH_PARAMETERS = [
  'access_token',
  'account_id',
  'associatedAccount',
  'expires_in',
  'expires_in_time',
  'id_token',
  'refresh_token',
  'state',
  'token_type',
]

const PENDING_ACCOUNT_KEY = 'auth_returnAssociatedAccount'
const SAVED_AT_KEY = 'auth_originalPathname_at'
const RETURN_PATH_TTL_MS = 10 * 60 * 1000
const isStale = savedAt => {
  if (!savedAt) return false
  const age = Date.now() - Number(savedAt)
  return Number.isFinite(age) && (age < 0 || age >= RETURN_PATH_TTL_MS)
}
const PENDING_ACCOUNT_TTL_MS = 10 * 60 * 1000

// A page the user was forced out of (UI library, VP-14220) is marked
// `auth_originalPathname_forced` = `<bounce|kept>:<saved at>[:<sub>]`. It lasts a
// day and goes back only to the user it names (a marker naming nobody is never
// restored); keep in step with the
// library's isSavedReturnPathUsable / isReturnPathFor (src/auth/authUrl.js).
const FORCED_KEY = 'auth_originalPathname_forced'
// The account the forced-out user was in; restored with the page.
const FORCED_ACCOUNT_KEY = 'auth_originalPathname_account'
const FORCED_TTL_MS = 24 * 60 * 60 * 1000
const tokenSubject = (idToken) => {
  try {
    const part = String(idToken || '').split('.')[1]
    if (!part) return null
    const base64 = part.replace(/-/g, '+').replace(/_/g, '/')
    const json = JSON.parse(atob(base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=')))
    return typeof json.sub === 'string' && json.sub ? json.sub : null
  } catch {
    return null
  }
}
const isForcedReturnBlocked = (marker, idToken) => {
  if (!marker) return false
  const match = /^(bounce|kept):(\d+)(?::([^:]+))?$/.exec(marker)
  if (!match) return true
  const age = Date.now() - Number(match[2])
  if (!(age >= 0 && age < FORCED_TTL_MS)) return true
  try {
    return !match[3] || tokenSubject(idToken) !== decodeURIComponent(match[3])
  } catch {
    return true
  }
}

// library-v1 `_sanitizeOriginalPathname`, plus `refresh_token`: 1.38.4 leaves the
// forwarded refresh token in the saved path until sign-in completes.
const sanitize = pathname => {
  const [path, hash] = pathname.split('#')
  if (!hash || !AUTH_HASH_PARAMETERS.some(param => hash.includes(`${param}=`))) {
    return pathname
  }
  const params = new URLSearchParams(hash)
  AUTH_HASH_PARAMETERS.forEach(param => params.delete(param))
  const sanitizedHash = params.toString()
  return path + (sanitizedHash ? `#${sanitizedHash}` : '')
}

const isSameOriginPath = path => path.startsWith('/') && !path.startsWith('//')

// True when the library will send the user to the login page: no stored ID token,
// or one that has expired.
const willBounceToLogin = win => {
  try {
    const token = win.localStorage.getItem('auth_userToken')
    if (!token) return true
    const payload = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')
    return JSON.parse(win.atob(payload)).exp * 1000 <= Date.now()
  } catch {
    return true
  }
}

/**
 * Puts the pre-login path back in the address bar before vue-router reads it.
 *
 * Studio opens this app with no tokens when the session can renew (VP-14220), so
 * the app bounces through the login page. library-v1 saves the deep link in
 * `localStorage.auth_originalPathname` and restores it with `history.replaceState`
 * inside `app.use(IdmUI)`, after `createWebHistory()` has already captured the
 * return URL. vue-router never sees that later `replaceState`, so the deep link was
 * lost. This mirrors the library's relocation (same sanitizing, same hash merge)
 * and runs first; the library then finds the path already correct and does nothing.
 *
 * library-v1 also strips `associatedAccount` from the saved path (VP-10850) and
 * clears it from storage before the login redirect, so the bounce would open the
 * deep link as the user's main account. The account from a token-less deep link is
 * kept in this tab's `sessionStorage` when a login bounce is due, and put back on
 * the return to that same path, once, within 10 minutes. The APIs still check the
 * user's access to that account.
 *
 * Only relocates on a login return (`id_token` in the fragment) and only to
 * same-origin absolute paths.
 *
 * @example
 * // localStorage.auth_originalPathname = "/123?libraryId=7"
 * // location: "/456#id_token=…"  →  "/123?libraryId=7#associatedAccount=42&id_token=…"
 */
export function restoreAuthReturnPath(win = window) {
  try {
    const [currentBase, fragment = ''] = win.location.href.split('#')
    const currentPath = currentBase.replace(win.location.origin, '')
    const params = new URLSearchParams(fragment)

    if (!params.has('id_token')) {
      const associatedAccount = params.get('associatedAccount')
      if (associatedAccount && willBounceToLogin(win)) {
        win.sessionStorage.setItem(
          PENDING_ACCOUNT_KEY,
          JSON.stringify({ path: currentPath, associatedAccount, at: Date.now() })
        )
      }
      return false
    }

    const pending = JSON.parse(win.sessionStorage.getItem(PENDING_ACCOUNT_KEY) || 'null')
    win.sessionStorage.removeItem(PENDING_ACCOUNT_KEY)

    const original = sanitize(win.localStorage.getItem('auth_originalPathname') || '')
    if (!original || !isSameOriginPath(original)) return false
    // library-v1 ≥ 1.38.5 ignores a saved path older than 10 minutes (an abandoned
    // login, VP-14220); a missing timestamp counts as fresh.
    if (isStale(win.localStorage.getItem(SAVED_AT_KEY))) return false
    if (isForcedReturnBlocked(win.localStorage.getItem(FORCED_KEY), params.get('id_token'))) return false

    // An account in the login return wins over a remembered one, under either alias
    // (the library reads associatedAccount || account_id).
    const returnsAccount = params.has('associatedAccount') || params.has('account_id')
    let restored = original
    if (
      pending?.path === original.split('#')[0] &&
      Date.now() - pending.at < PENDING_ACCOUNT_TTL_MS &&
      !returnsAccount
    ) {
      const account = `associatedAccount=${encodeURIComponent(pending.associatedAccount)}`
      restored += (restored.includes('#') ? '&' : '#') + account
    }
    const forcedAccount = win.localStorage.getItem(FORCED_KEY) && win.localStorage.getItem(FORCED_ACCOUNT_KEY)
    if (forcedAccount && !returnsAccount && !/(^|[#&])(associatedAccount|account_id)=/.test(restored)) {
      restored += `${restored.includes('#') ? '&' : '#'}associatedAccount=${encodeURIComponent(forcedAccount)}`
    }

    // Compared with the hash included, as the library does: a root deep link
    // ("/#workspaceId=…") still has to be put back.
    if (restored === currentPath) return false

    win.localStorage.removeItem('auth_originalPathname')
    win.localStorage.removeItem(SAVED_AT_KEY)
    win.localStorage.removeItem(FORCED_KEY)
    win.localStorage.removeItem(FORCED_ACCOUNT_KEY)
    win.history.replaceState(
      null,
      '',
      `${restored}${restored.includes('#') ? '&' : '#'}${fragment}`
    )
    return true
  } catch {
    return false
  }
}
